import { SpeedInsights } from "@vercel/speed-insights/react"
import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Bell, HelpCircle, Menu, User, Clock, MessageCircle, X } from 'lucide-react';
import ToastContainer from './ToastContainer';
import Portal from './Portal';
import useToast from '../hooks/useToast';
import { motion } from 'framer-motion';
import { getAuth, signOut } from 'firebase/auth';
import { getDatabase, ref, set } from 'firebase/database';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import Sidebar from './Sidebar';
import Dashboard from './Dashboard';
import RoleBasedDashboard from './RoleBasedDashboard';
import Planning from './Planning';
import EmployeeManagement from './EmployeeManagement';
import AdaptiveSettings from './AdaptiveSettings';
import SubscriptionModuleConfig from './SubscriptionModuleConfig';
import EmployeeReports from './EmployeeReports';
import AdvancedRolesManagementPage from './roles-management/AdvancedRolesManagementPage';
import PlanningManagementSettings from './PlanningManagementSettings';
import { doc, getDoc, updateDoc, serverTimestamp, collection, query, where, getDocs, orderBy } from 'firebase/firestore';
import { db, storage } from '../firebase';
import ClientDiscussion from './ClientDiscussion';
import ClientNotes from './ClientNotes';
import ClientContract from './ClientContract';
import ClientReport from './ClientReport';
import ClientPlanning from './ClientPlanning';
import ClientSummary from './ClientSummary';
import useSyncEmailWithFirestore from '../hooks/useSyncEmailWithFirestore';
import AdvancedTaskManager from './AdvancedTaskManager';
import usePresence from '../hooks/usePresence';
import useInactivityTimer from '../hooks/useInactivityTimer';
import ClientForm from './ClientForm';
import NFCModal from './NFCModal';
import ClientAuthManager from './ClientAuthManager';
import ViewClientModal from './modals/ViewClientModal';
import AccountingManagement from './AccountingManagement';
import ResourceManagementEnhanced from './ResourceManagementEnhanced';
import { usePushNotifications } from '../hooks/usePushNotifications';
import MapView from './MapView';
import DashboardTemplateManager from './DashboardTemplateManager';
import MessagingPage from './MessagingPage';
import { useMessaging } from '../contexts/MessagingContext';
import '../MainLayout.css';

// Composant de notification pour les messages non lus
const MessageNotification = ({ unreadCount, onClick, onDismiss }) => {
  const isDarkMode = document.documentElement.classList.contains('dark');

  if (unreadCount === 0) return null;

  return (
    <div
      className={`fixed bottom-4 right-4 z-50`}
      style={{
        animation: 'slideInUp 0.3s ease-out'
      }}
    >
      <div
        className={`flex items-center gap-3 px-4 py-3 rounded-lg shadow-lg border cursor-pointer ${isDarkMode
            ? 'bg-blue-900 border-blue-700 text-white'
            : 'bg-blue-50 border-blue-200 text-blue-900'
          }`}
        onClick={onClick}
      >
        <div className={`p-2 rounded-full ${isDarkMode ? 'bg-blue-800' : 'bg-blue-100'}`}>
          <Bell size={20} className={isDarkMode ? 'text-blue-200' : 'text-blue-600'} />
        </div>
        <div className="flex-1">
          <p className={`font-semibold text-sm ${isDarkMode ? 'text-white' : 'text-blue-900'}`}>
            {unreadCount} nouveau{unreadCount > 1 ? 'x' : ''} message{unreadCount > 1 ? 's' : ''}
          </p>
          <p className={`text-xs ${isDarkMode ? 'text-blue-200' : 'text-blue-700'}`}>
            Cliquez pour voir
          </p>
        </div>
        <button
          onClick={(e) => {
            e.stopPropagation();
            onDismiss();
          }}
          className={`p-1 rounded-full hover:bg-opacity-20 hover:bg-white transition-colors ${isDarkMode ? 'text-blue-200 hover:text-white' : 'text-blue-600 hover:text-blue-800'
            }`}
        >
          <X size={16} />
        </button>
      </div>
    </div>
  );
};

const MainLayout = () => {
  const { view = 'dashboard' } = useParams();
  const [currentView, setCurrentView] = useState(view);
  const location = useLocation();
  const { toasts, removeToast, showToast } = useToast();
  const [selectedClient, setSelectedClient] = useState(null);
  const [availableColors, setAvailableColors] = useState([]);
  const [generalSettings, setGeneralSettings] = useState({
    organizationName: 'AgenTime',
  });
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const sidebarRef = useRef(null);
  const [isProfileMenuOpen, setIsProfileMenuOpen] = useState(false);
  const [employeeData, setEmployeeData] = useState(null);
  const profileMenuRef = useRef(null);
  const navigate = useNavigate();
  const [lastLoginTime, setLastLoginTime] = useState(null);
  const [userPermissions, setUserPermissions] = useState({});
  const [notifications, setNotifications] = useState([]);
  const [isNotificationsOpen, setIsNotificationsOpen] = useState(false);
  const [modalType, setModalType] = useState(null);
  const [modalProps, setModalProps] = useState({});
  const resetInactivityTimer = useInactivityTimer(); // Utiliser le nouveau hook

  const notificationsRef = useRef(null);

  // Récupérer les compteurs de messages non lus
  const messagingContext = useMessaging();
  const unreadCounts = messagingContext?.unreadCounts || { internal: 0, client: 0, group: 0 };
  const totalUnread = unreadCounts.internal + unreadCounts.client + unreadCounts.group;

  // État pour les notifications push de messages
  const [showMessageNotification, setShowMessageNotification] = useState(true);
  const [previousUnreadCount, setPreviousUnreadCount] = useState(0);

  // Synchroniser l'état currentView avec l'URL
  useEffect(() => {
    setCurrentView(view);
  }, [view]);

  // Gérer les scans NFC qui arrivent via le state de navigation
  useEffect(() => {
    // Vérifier si on a des données NFC dans le state de navigation
    if (location.state?.nfcScan && location.state?.nfcData) {
      const { nfcData } = location.state;

      // Afficher une notification
      showToast({
        type: 'info',
        title: 'Scan NFC détecté',
        message: `Tag NFC pour le client ${nfcData.clientId} détecté. Redirection vers le planning...`,
        duration: 3000
      });

      // Rediriger vers le planning après un court délai
      setTimeout(() => {
        setCurrentView('planning');
        navigate('/dashboard/planning');
      }, 1500);

      // Nettoyer le state pour éviter de retraiter le scan
      navigate(location.pathname, { replace: true, state: {} });
    }

    // Vérifier aussi s'il y a des données NFC en attente dans sessionStorage
    const pendingNFCScan = sessionStorage.getItem('pendingNFCScan');
    if (pendingNFCScan) {
      try {
        const { nfcData, timestamp } = JSON.parse(pendingNFCScan);

        // Vérifier que le scan n'est pas trop ancien (moins de 5 minutes)
        const scanAge = Date.now() - new Date(timestamp).getTime();
        if (scanAge < 5 * 60 * 1000) {
          showToast({
            type: 'success',
            title: 'Scan NFC restauré',
            message: `Traitement du scan NFC pour le client ${nfcData.clientId}`,
            duration: 3000
          });

          setTimeout(() => {
            setCurrentView('planning');
            navigate('/dashboard/planning');
          }, 1500);
        }

        // Nettoyer le sessionStorage
        sessionStorage.removeItem('pendingNFCScan');
      } catch (error) {
        console.error('Erreur lors de la récupération du scan NFC en attente:', error);
        sessionStorage.removeItem('pendingNFCScan');
      }
    }
  }, [location.state, navigate, location.pathname, showToast]);

  // Gérer l'affichage de la notification quand le nombre de messages non lus change
  useEffect(() => {
    // Si le nombre de messages non lus augmente, afficher la notification
    if (totalUnread > previousUnreadCount && totalUnread > 0) {
      setShowMessageNotification(true);

      // Auto-masquer la notification après 10 secondes si pas dans la messagerie
      if (currentView !== 'messaging') {
        const timer = setTimeout(() => {
          setShowMessageNotification(false);
        }, 10000);

        return () => clearTimeout(timer);
      }
    }

    // Si on est dans la messagerie, masquer la notification
    if (currentView === 'messaging') {
      setShowMessageNotification(false);
    }

    setPreviousUnreadCount(totalUnread);
  }, [totalUnread, currentView, previousUnreadCount]);

  // Fonction pour gérer le clic sur la notification
  const handleMessageNotificationClick = () => {
    setShowMessageNotification(false);
    handleViewChange('messaging');
  };

  // Fonction pour fermer la notification
  const handleDismissMessageNotification = () => {
    setShowMessageNotification(false);
  };

  // Sauvegarder le client sélectionné dans le localStorage
  useEffect(() => {
    if (selectedClient) {
      localStorage.setItem('selectedClient', JSON.stringify(selectedClient));
    }
  }, [selectedClient]);

  // Restaurer le client sélectionné au chargement pour toutes les vues client
  useEffect(() => {
    const savedClient = localStorage.getItem('selectedClient');
    const clientViews = ['clientSummary', 'clientDiscussion', 'clientNotes', 'clientContract', 'clientReport', 'clientPlanning'];

    if (savedClient && clientViews.includes(view)) {
      try {
        const clientData = JSON.parse(savedClient);
        setSelectedClient(clientData);
      } catch (error) {
        console.error('Erreur lors de la restauration du client:', error);
        localStorage.removeItem('selectedClient');
      }
    }
  }, [view]);

  // Mettre à jour l'URL quand currentView change
  const handleViewChange = (newView) => {
    setCurrentView(newView);
    navigate(`/dashboard/${newView}`);
  };

  const openModal = (component, props = {}) => {
    // Si component est une string, l'utiliser directement comme identifiant
    if (typeof component === 'string') {
      setModalType(component);
      setModalProps(props);
      return;
    }

    // Utiliser une approche simple basée sur le nom du composant
    // Même minifié, on peut essayer de détecter le type par le contenu de la fonction
    const componentString = component?.toString() || '';
    let modalIdentifier = 'UnknownModal';

    if (componentString.includes('ClientForm') || componentString.includes('client') && componentString.includes('usedColors')) {
      modalIdentifier = 'ClientForm';
    } else if (componentString.includes('NFCModal') || componentString.includes('nfc')) {
      modalIdentifier = 'NFCModal';
    } else if (componentString.includes('DevisGenerator') || componentString.includes('devis')) {
      modalIdentifier = 'DevisGenerator';
    } else if (componentString.includes('ClientAuthManager') || componentString.includes('auth') && componentString.includes('client')) {
      modalIdentifier = 'ClientAuthManager';
    } else if (componentString.includes('ViewClientModal')) {
      modalIdentifier = 'ViewClientModal';
    }

    setModalType(modalIdentifier);
    setModalProps(props || {});
  };

  const closeModal = () => {
    setModalType(null);
    setModalProps({});
  };

  usePresence();
  useSyncEmailWithFirestore();
  //usePushNotifications(db, employeeData); // Réactivé avec configuration iOS

  useEffect(() => {
    const auth = getAuth();

    const unsubscribe = auth.onAuthStateChanged(async (user) => {
      if (user) {
        try {
          const employeeDoc = await getDoc(doc(db, 'employees', user.uid));
          if (employeeDoc.exists()) {
            const data = { id: employeeDoc.id, ...employeeDoc.data() };

            // Préparer TOUTES les données avant les setState pour éviter les re-renders multiples
            // Mise à jour du lastLogin (fire-and-forget, pas besoin d'attendre)
            updateDoc(doc(db, 'employees', user.uid), {
              lastLogin: serverTimestamp()
            }).catch(() => { });

            // Préparer les permissions AVANT les setState
            let newPermissions = {};
            try {
              const advancedRoleDoc = await getDoc(doc(db, 'advanced_roles', data.role));
              if (advancedRoleDoc.exists()) {
                const roleData = advancedRoleDoc.data();
                if (roleData.permissions) {
                  Object.entries(roleData.permissions).forEach(([resource, perms]) => {
                    Object.entries(perms).forEach(([action, hasPermission]) => {
                      newPermissions[`${resource}_${action}`] = hasPermission;
                    });
                  });
                }
              }
            } catch (roleError) {
              console.warn('Erreur chargement rôle avancé:', roleError);
            }

            // Faire tous les setState consécutivement (React 18 les batch automatiquement)
            setEmployeeData(data);
            setLastLoginTime(data.lastLogin);
            setUserPermissions(newPermissions);
          } else {
            setEmployeeData(null);
            setUserPermissions({});
          }
        } catch (error) {
          console.error("Erreur lors du chargement des données utilisateur:", error);
          setEmployeeData(null);
          setUserPermissions({});
        }
      } else {
        setEmployeeData(null);
        setUserPermissions({});
        setLastLoginTime(null);
      }
    });

    return () => unsubscribe();
  }, [navigate, db]);


  const hasPermission = useCallback((permission) => {
    // Le nouveau système utilise uniquement le format objet {resource, action}
    if (typeof permission === 'object' && permission.resource && permission.action) {
      const { resource, action } = permission;
      const permissionKey = `${resource}_${action}`;

      // Vérifier la permission spécifique
      return !!userPermissions[permissionKey];
    }
    return false;
  }, [userPermissions]);

  const formatLastLoginTime = (timestamp) => {
    if (!timestamp) return 'Première connexion';
    try {
      // Vérifier si timestamp est un objet Firestore Timestamp
      if (timestamp?.toDate) {
        return timestamp.toDate().toLocaleString('fr-FR', {
          year: 'numeric',
          month: 'long',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit'
        });
      }
      // Si c'est déjà une date
      if (timestamp instanceof Date) {
        return timestamp.toLocaleString('fr-FR', {
          year: 'numeric',
          month: 'long',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit'
        });
      }
      // Si c'est un timestamp numérique
      if (typeof timestamp === 'number') {
        return new Date(timestamp).toLocaleString('fr-FR', {
          year: 'numeric',
          month: 'long',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit'
        });
      }
      return 'Date non valide';
    } catch (error) {
      console.error("Erreur lors du formatage de la date:", error);
      return 'Date non valide';
    }
  };

  const fetchNotifications = useCallback(async () => {
    // Désactivation temporaire des notifications de rendez-vous
    // Le système de notifications sera modifié ultérieurement
    setNotifications([]);
  }, []);

  useEffect(() => {
    if (employeeData?.id) {
      fetchNotifications();
    }
  }, [fetchNotifications, employeeData]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      // Vérifier si le clic est dans un portal (dropdown)
      const isInPortal = event.target.closest('#profile-portal, #notifications-portal');

      if (!isInPortal) {
        if (profileMenuRef.current && !profileMenuRef.current.contains(event.target)) {
          setIsProfileMenuOpen(false);
        }
        if (notificationsRef.current && !notificationsRef.current.contains(event.target)) {
          setIsNotificationsOpen(false);
        }
      }
      // NE PAS fermer le sidebar automatiquement - seulement via la croix
    };

    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleUserActivity = useCallback(() => {
    resetInactivityTimer();
  }, [resetInactivityTimer]);

  useEffect(() => {
    // Ajouter les écouteurs d'événements une seule fois
    window.addEventListener('mousemove', handleUserActivity);
    window.addEventListener('keydown', handleUserActivity);
    window.addEventListener('mousedown', handleUserActivity);
    window.addEventListener('touchstart', handleUserActivity);

    return () => {
      // Nettoyer les écouteurs d'événements
      window.removeEventListener('mousemove', handleUserActivity);
      window.removeEventListener('keydown', handleUserActivity);
      window.removeEventListener('mousedown', handleUserActivity);
      window.removeEventListener('touchstart', handleUserActivity);
    };
  }, [handleUserActivity]);


  const handleLogout = async () => {
    const auth = getAuth();
    const realtimeDb = getDatabase();

    try {
      const user = auth.currentUser;

      if (user) {
        // Mettre à jour le statut de présence à false avec timeout
        const userStatusRef = ref(realtimeDb, `presence/${user.uid}`);

        // Ajouter un timeout pour éviter le blocage
        const presencePromise = set(userStatusRef, false);
        const timeoutPromise = new Promise((_, reject) =>
          setTimeout(() => reject(new Error('Timeout mise à jour présence')), 3000)
        );

        try {
          await Promise.race([presencePromise, timeoutPromise]);
        } catch (presenceError) {
          // Continuer même si la mise à jour de présence échoue
        }
      }

      // Déconnecter l'utilisateur
      await signOut(auth);

      // Rediriger vers la page de connexion
      navigate('/login');
    } catch (error) {
      // En cas d'erreur, forcer la redirection
      window.location.href = '/login';
    }
  };

  useEffect(() => {
    const fetchGeneralSettings = async () => {
      try {
        const settingsDoc = await getDoc(doc(db, 'settings', 'general'));
        if (settingsDoc.exists()) {
          const settingsData = settingsDoc.data();
          setGeneralSettings(settingsData);
        }
      } catch (error) {
        console.error("Erreur lors du chargement des paramètres généraux:", error);
      }
    };

    fetchGeneralSettings();
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setSelectedClient(prev => ({ ...prev, [name]: value }));
  };

  const handleSaveClient = async () => {
    // Logique pour sauvegarder le client
    // Vous devrez implémenter cette fonction
  };

  // Utiliser useRef pour stocker les données sans causer de re-renders du ContentComponent
  const employeeDataRef = useRef(employeeData);
  const userPermissionsRef = useRef(userPermissions);
  const hasPermissionRef = useRef(hasPermission);
  const selectedClientRef = useRef(selectedClient);

  // Synchroniser les refs avec les valeurs actuelles
  employeeDataRef.current = employeeData;
  userPermissionsRef.current = userPermissions;
  hasPermissionRef.current = hasPermission;
  selectedClientRef.current = selectedClient;

  const ContentComponent = useMemo(() => {
    // Vérifier l'accès aux paramètres généraux
    if (currentView === 'generalSettings' && !hasPermission({ resource: 'settings', action: 'write' })) {
      return () => (
        <div className="flex items-center justify-center h-full">
          <div className="text-center p-8 bg-white rounded-lg shadow-md">
            <h2 className="text-2xl font-bold text-red-600 mb-4">Accès refusé</h2>
            <p className="text-gray-600 mb-4">
              Vous n'avez pas les permissions nécessaires pour accéder aux paramètres généraux.
            </p>
            <button
              onClick={() => setCurrentView('dashboard')}
              className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
            >
              Retour au tableau de bord
            </button>
          </div>
        </div>
      );
    }

    const components = {
      dashboard: (props) => <RoleBasedDashboard {...props} />,
      planning: (props) => <Planning {...props} currentUser={employeeDataRef.current} />,
      employeeList: EmployeeManagement,
      clientSummary: () => (
        <ClientSummary
          client={selectedClientRef.current}
          db={db}
          currentUser={employeeDataRef.current}
          handleInputChange={handleInputChange}
          handleSave={handleSaveClient}
          availableColors={availableColors}
          hasPermission={hasPermissionRef.current}
        />
      ),
      clientDiscussion: (props) => <ClientDiscussion {...props} client={selectedClientRef.current} />,
      clientNotes: () => (
        <ClientNotes
          db={db}
          client={selectedClientRef.current}
          currentUser={employeeDataRef.current}
          hasPermission={hasPermissionRef.current}
        />
      ),
      clientContract: (props) => <ClientContract {...props} client={selectedClientRef.current} />,
      clientReport: () => (
        <ClientReport
          client={selectedClientRef.current}
          db={db}
        />
      ),
      clientPlanning: () => (
        <ClientPlanning
          db={db}
          client={selectedClientRef.current}
          userPermissions={userPermissionsRef.current}
        />
      ),
      generalSettings: AdaptiveSettings,
      subscriptionModuleConfig: () => <SubscriptionModuleConfig />,
      employeeReports: EmployeeReports,
      rolesManagement: (props) => <AdvancedRolesManagementPage {...props} currentUser={employeeDataRef.current} />,
      planningsettings: PlanningManagementSettings,
      TaskManager: (props) => <AdvancedTaskManager {...props} db={db} currentUser={employeeDataRef.current} hasPermission={hasPermissionRef.current} />,
      mapView: (props) => <MapView {...props} db={db} currentUser={employeeDataRef.current} />,
      accounting: (props) => <AccountingManagement {...props} />,
      resources: (props) => <ResourceManagementEnhanced {...props} db={db} currentUser={employeeDataRef.current} />,
      dashboardTemplates: (props) => <DashboardTemplateManager {...props} db={db} currentUser={employeeDataRef.current} />,
      messaging: (props) => <MessagingPage {...props} db={db} currentUser={employeeDataRef.current} />,
    };

    const Component = components[currentView] || Dashboard;

    return (props) => (
      <Component
        db={db}
        storage={storage}
        onSettingsUpdate={setGeneralSettings}
        setCurrentView={setCurrentView}
        setSelectedClient={setSelectedClient}
        currentUser={employeeDataRef.current}
        {...props}
      />
    );
  }, [currentView]); // Seulement re-créer quand on change de vue, pas quand employeeData change

  const toggleSidebar = () => {
    setIsSidebarOpen(prev => !prev);
  };


  const getDisplayName = () => {
    if (!employeeData) return 'Chargement...';

    // Vérifications de sécurité pour les noms
    const firstName = employeeData.firstName || '';
    const lastName = employeeData.lastName || '';

    // Pour les écrans larges, retourner le nom complet
    const fullName = `${firstName} ${lastName}`.trim();

    // Pour les petits écrans, retourner les initiales ou le prénom
    const firstInitial = firstName[0] || '';
    const lastInitial = lastName[0] || '';
    const initials = `${firstInitial}${lastInitial}`;

    return (
      <>
        {/* Nom complet sur écrans larges */}
        <span className="hidden lg:inline">{fullName || 'Utilisateur'}</span>
        {/* Prénom seulement sur écrans moyens */}
        <span className="hidden md:inline lg:hidden">{firstName || 'Utilisateur'}</span>
        {/* Initiales sur petits écrans */}
        <span className="md:hidden">{initials || 'U'}</span>
      </>
    );
  };

  // Fonction pour déterminer l'affichage du profil en mode mobile
  const getMobileProfileDisplay = () => {
    if (!employeeData) return null;

    // En mode mobile, si l'utilisateur a une photo, afficher uniquement la photo
    if (employeeData.photoURL) {
      return (
        <img
          src={employeeData.photoURL}
          alt="Profile"
          className="ml-profile-image mobile-photo-only"
        />
      );
    }

    // Sinon, afficher uniquement les initiales
    const firstName = employeeData.firstName || '';
    const lastName = employeeData.lastName || '';
    const firstInitial = firstName[0] || '';
    const lastInitial = lastName[0] || '';
    const initials = `${firstInitial}${lastInitial}`;

    return (
      <div className="ml-profile-initials mobile-initials-only">
        {initials || 'U'}
      </div>
    );
  };

  useEffect(() => {
    // Logique pour suivre les mises à jour de l'utilisateur
  }, [employeeData]);


  return (
    <div className="ml-layout">
      <header className="ml-header bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
        <div className="ml-header-content">
          <div className="ml-logo-container">
            <button
              className="ml-menu-button text-gray-600 dark:text-gray-300 hover:text-blue-500 dark:hover:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20"
              onClick={toggleSidebar}
            >
              <Menu size={24} />
            </button>
            {generalSettings.useLogoInHeader && generalSettings.logoUrl ? (
              <div className="ml-header-logo">
                <img
                  src={generalSettings.logoUrl}
                  alt={generalSettings.organizationName || "Logo de l'organisation"}
                  style={{
                    height: `${generalSettings.logoDisplayHeight || 32}px`,
                    width: 'auto'
                  }}
                  className="ml-logo-image"
                />
                <span className="ml-logo-org-name sr-only">
                  {generalSettings.organizationName}
                </span>
              </div>
            ) : (
              <h1 className="ml-org-name text-gray-900 dark:text-white">{generalSettings.organizationName}</h1>
            )}
          </div>
          <div className="ml-header-actions">
            {/* Bouton Messagerie avec badge */}
            <button
              className="ml-icon-button relative text-gray-600 dark:text-gray-200 hover:text-blue-500 dark:hover:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20"
              onClick={() => handleViewChange('messaging')}
              title="Messagerie"
            >
              <MessageCircle size={24} />
              {totalUnread > 0 && (
                <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs font-bold rounded-full h-5 min-w-[20px] flex items-center justify-center px-1">
                  {totalUnread > 99 ? '99+' : totalUnread}
                </span>
              )}
            </button>

            <div className="relative" ref={notificationsRef}>
              <button
                className="ml-icon-button relative text-gray-600 dark:text-gray-200 hover:text-blue-500 dark:hover:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20"
                onClick={() => setIsNotificationsOpen(!isNotificationsOpen)}
              >
                <Bell size={24} />
                {/* Badges séparés pour Messagerie et Clients */}
                <div className="ml-notification-badges-container">
                  {notifications.length > 0 && (
                    <>
                      <span className="ml-notification-badge messaging-badge">
                        3
                      </span>
                      <span className="ml-notification-badge clients-badge">
                        5
                      </span>
                    </>
                  )}
                </div>
              </button>
              {isNotificationsOpen && (
                <Portal id="notifications-portal">
                  <div
                    className="ml-notifications-dropdown bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700"
                    style={{ pointerEvents: 'auto' }}
                    onClick={(e) => e.stopPropagation()}
                    onMouseDown={(e) => e.stopPropagation()}
                  >
                    <div className="ml-notifications-header bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white border-gray-200 dark:border-gray-600">
                      Notifications
                    </div>
                    <div className="max-h-64 overflow-y-auto">
                      {notifications.length === 0 ? (
                        <p className="ml-notification-item text-gray-500 dark:text-gray-400">Aucune notification</p>
                      ) : (
                        notifications.map(notif => (
                          <div key={notif.id} className="ml-notification-item text-gray-700 dark:text-gray-300 hover:bg-blue-50 dark:hover:bg-blue-900/20">
                            <p className="text-sm">{notif.message}</p>
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                </Portal>
              )}
            </div>
            <div className="ml-profile-menu" ref={profileMenuRef}>
              <button
                className="ml-profile-button text-gray-600 dark:text-gray-200 hover:text-blue-500 dark:hover:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20"
                onClick={() => setIsProfileMenuOpen(!isProfileMenuOpen)}
              >
                {/* Affichage mobile : photo uniquement ou initiales uniquement */}
                <div className="md:hidden">
                  {getMobileProfileDisplay()}
                </div>

                {/* Affichage desktop : photo ou icône avec nom */}
                <div className="hidden md:flex md:items-center">
                  {employeeData?.photoURL ? (
                    <img src={employeeData.photoURL} alt="Profile" className="ml-profile-image border-gray-200 dark:border-gray-600" />
                  ) : (
                    <User size={24} className="ml-profile-icon" />
                  )}
                  <span className="ml-profile-name text-gray-900 dark:text-white">{getDisplayName()}</span>
                </div>
              </button>

              {isProfileMenuOpen && (
                <Portal id="profile-portal">
                  <div
                    className="ml-profile-dropdown bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700"
                    style={{ pointerEvents: 'auto' }}
                    onClick={(e) => e.stopPropagation()}
                    onMouseDown={(e) => e.stopPropagation()}
                  >
                    <div className="ml-dropdown-item text-gray-700 dark:text-gray-300 border-gray-100 dark:border-gray-600">
                      <p className="text-gray-500 dark:text-gray-400 text-sm mb-1">Dernière connexion :</p>
                      <p className="flex items-center text-sm text-gray-700 dark:text-gray-300">
                        <Clock size={16} className="mr-2" />
                        <span>{formatLastLoginTime(lastLoginTime)}</span>
                      </p>
                    </div>

                    <div className="py-1">
                      <button
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setIsProfileMenuOpen(false);
                          setTimeout(() => {
                            navigate('/profile');
                          }, 100);
                        }}
                        className="ml-dropdown-item text-gray-700 dark:text-gray-300 hover:bg-blue-50 dark:hover:bg-blue-900/20 hover:text-blue-500 dark:hover:text-blue-400 border-gray-100 dark:border-gray-600 w-full text-left"
                      >
                        Profil
                      </button>
                    </div>

                    <div className="border-t border-gray-200 dark:border-gray-600">
                      <button
                        onClick={handleLogout}
                        className="ml-dropdown-item ml-logout-button text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20"
                      >
                        Déconnexion
                      </button>
                    </div>
                  </div>
                </Portal>
              )}
            </div>
          </div>
        </div>
      </header>
      <div className="ml-content">
        <div ref={sidebarRef}>
          <Sidebar
            setCurrentView={setCurrentView}
            currentView={currentView}
            isOpen={isSidebarOpen}
            toggleSidebar={toggleSidebar}
            selectedClient={selectedClient}
            setSelectedClient={setSelectedClient}
            currentUser={employeeData}
            setIsSidebarOpen={setIsSidebarOpen}
            handleViewChange={handleViewChange}
          />
        </div>
        <motion.main
          className="ml-main-content"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
        >
          <div className="ml-scroll-container">
            <ContentComponent />
          </div>
        </motion.main>
      </div>

      {/* Modal global */}
      {modalType && (
        <>
          {/* ViewClientModal gère son propre overlay */}
          {modalType === 'ViewClientModal' ? (
            <ViewClientModal
              {...(modalProps || {})}
              onClose={closeModal}
            />
          ) : (
            <div
              className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50 p-4"
              onClick={closeModal} // Fermer en cliquant en dehors
            >
              <div
                className="bg-white rounded-lg shadow-xl w-fit max-w-4xl max-h-[90vh] overflow-y-auto"
                onClick={(e) => e.stopPropagation()} // Empêcher la fermeture en cliquant à l'intérieur
              >
                <div className="p-6">
                  {modalType === 'ClientForm' && (
                    <ClientForm
                      {...(modalProps || {})}
                      onClose={closeModal}
                    />
                  )}
                  {modalType === 'NFCModal' && (
                    <NFCModal
                      {...(modalProps || {})}
                      onClose={closeModal}
                    />
                  )}
                  {modalType === 'ClientAuthManager' && (
                    <ClientAuthManager
                      {...(modalProps || {})}
                      onClose={closeModal}
                    />
                  )}
                  {modalType === 'UnknownModal' && (
                    <div className="text-center p-8">
                      <h3 className="text-lg font-semibold text-red-600">Erreur: Modal non reconnu</h3>
                      <p className="text-gray-600">Type de modal: {modalType}</p>
                      <p className="text-gray-600">Props: {JSON.stringify(modalProps)}</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </>
      )}

      {/* Toast Notifications */}
      <ToastContainer toasts={toasts} removeToast={removeToast} />

      {/* Notification push de nouveaux messages */}
      {showMessageNotification && totalUnread > 0 && currentView !== 'messaging' && (
        <MessageNotification
          unreadCount={totalUnread}
          onClick={handleMessageNotificationClick}
          onDismiss={handleDismissMessageNotification}
        />
      )}
    </div>
  );
};

export default MainLayout;
